import { StackScreenProps } from "@react-navigation/stack";
import { DocketTemplateFormValues } from "../../components/DocketTemplateForm/types";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import React from "react";
import { Colors } from "react-native-ui-lib";
import {
  Button,
  Card,
  Divider,
  FlatActionList,
  Page,
  Stack,
} from "@towersystems/roam-common-native";
import { DocketTemplateForm } from "../../components/DocketTemplateForm";
import { useManageDocketTemplateScreen } from "../ManageDocketTemplateRootScreen/hooks";
import { useMutation } from "@apollo/client";

import { DocketTemplate } from "@towersystems/roam-common/lib/generated-types";
import { TerminalIdsInput } from "../../components/DocketTemplateForm/components/TerminalIdsInput";
import {
  MUTATION_CREATE_DOCKET_TEMPLATE,
  MUTATION_UPDATE_DOCKET_TEMPLATE,
} from "../../common/constants";

export type ManageDocketTemplateScreenProps = StackScreenProps<
  any,
  "ManageDocketTemplateScreen"
>;

export const ManageDocketTemplateScreen = ({
  navigation,
}: ManageDocketTemplateScreenProps) => {
  const maybeContext = useManageDocketTemplateScreen();

  const { watch, setError, handleSubmit, control, formState, getValues } =
    useFormContext<DocketTemplateFormValues>();

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const { t } = useTranslation("settings");
  const watchedId = watch("id");
  const watchedFilters = watch("filters");

  const [mutationCreateDocketTemplate] = useMutation<{
    createDocketTemplate: DocketTemplate;
  }>(MUTATION_CREATE_DOCKET_TEMPLATE);

  const [mutationUpdateDocketTemplate] = useMutation<{
    updateDocketTemplate: DocketTemplate;
  }>(MUTATION_UPDATE_DOCKET_TEMPLATE);

  React.useEffect(() => {
    if (watchedId) {
      navigation.setOptions({
        headerRight: () => (
          <Button
            label={t("button.delete_docket_template")}
            isDestructive
            onPress={() => {
              handleOnDelete(watchedId);
            }}
            size="compact"
          ></Button>
        ),
      });
    }
  }, [watchedId]);

  const handleOnDelete = React.useCallback(async (id: string) => {}, []);

  const handleOnSubmit = React.useCallback(
    async (data: DocketTemplateFormValues) => {
      const isCreate = !watchedId;

      const commonVariables = {
        name: data.name,
        terminalIds: data.terminalIds,
        automaticallyPrint: data.automaticallyPrint,
        filters: data.filters.map((filter) => {
          return {
            type: filter.type,
            values: filter.values.map(({ value }) => value),
          };
        }),
      };

      try {
        if (isCreate) {
          const result = await mutationCreateDocketTemplate({
            variables: {
              input: {
                ...commonVariables,
              },
            },
          });
          if (result.data) {
            maybeContext?.onAfterSubmit?.(result.data.createDocketTemplate);
          }
        } else {
          const result = await mutationUpdateDocketTemplate({
            variables: {
              input: {
                id: watchedId,
                ...commonVariables,
              },
            },
          });
          if (result.data) {
            maybeContext?.onAfterSubmit?.(result.data.updateDocketTemplate);
          }
        }
      } catch (e) {
        setError("name", {
          type: "manual",
        });
      }
    },
    [watchedId]
  );
  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const detailsContent = (
    <Card title={t("card.docket_template_form_details.title")}>
      <Stack vertical>
        <DocketTemplateForm.NameInputField />
        <Divider />
        <DocketTemplateForm.AutomaticallyPrintInput />
      </Stack>
    </Card>
  );

  const terminalIdsContent = (
    <Card title={t("card.docket_template_form_terminal_ids.title")}>
      <TerminalIdsInput />
    </Card>
  );

  const filtersContent = (
    <Card title={t("card.docket_template_form_filters.title")}>
      <Stack vertical>
        <Button
          isAction
          label={t("button.add_docket_template_filter")}
          onPress={() =>
            navigation.navigate("ManageDocketTemplateFilterRootScreen")
          }
        ></Button>
        <FlatActionList
          items={watchedFilters.map((filter, index) => {
            return {
              key: `${filter.type}-${index}`,
              title: `${filter.type} filter`,
              subTitle: `${filter.values.length} values`,
              onPress: () =>
                navigation.navigate("ManageDocketTemplateFilterRootScreen", {
                  index,
                }),
            };
          })}
        />
      </Stack>
    </Card>
  );

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_docket_template")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title={
        watchedId
          ? t("title.edit_docket_template")
          : t("title.create_docket_template")
      }
      footer={footerMarkup}
    >
      <Stack vertical>
        {detailsContent}
        {terminalIdsContent}
        {filtersContent}
      </Stack>
    </Page>
  );
};
